import { useMemo } from 'react';
import { Box, IconButton, Tooltip } from '@material-ui/core';
import CloseRoundedIcon from '@material-ui/icons/CloseRounded';

import { SButton, SText } from '@setvi/shared/components';
import { DownloadIconV2, InfoIconV2 } from '@setvi/shared/components/sicons';
import { useIntercom } from 'react-use-intercom';

import { useAppContext } from 'Providers/AppProvider/AppContext';
import { useRFQStore } from 'pages/auto-rfq/store';

const FAILED_COLOR = '#FA2020';

interface FileDetailsDialogProps {
  onClose: () => void;
}

/**
 * Lists the RFQ's failed files so the user can re-download the original upload
 * and retry, with a Live Help (Intercom) fallback. Opened from the failed
 * RFQStatusBanner's "file details" link. Reads the failed files straight from
 * the RFQ store.
 */
export const FileDetailsDialog = ({ onClose }: FileDetailsDialogProps) => {
  const { user } = useAppContext();
  const { boot, show } = useIntercom();
  const { files } = useRFQStore();

  const { failedFiles, totalCount } = useMemo(
    () => ({
      failedFiles: files?.filter(f => f?.status === 'FAILED') ?? [],
      totalCount: files?.length ?? 0
    }),
    [files]
  );

  const openLiveHelp = () => {
    boot({
      alignment: 'left',
      userId: user?.ID?.toString(),
      email: user?.Email,
      name: user?.FullName
    });
    show();
  };

  const downloadOriginal = (url?: string | null, name?: string) => {
    if (!url) return;
    const link = document.createElement('a');
    link.href = url;
    link.download = name || 'file';
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Box style={{ width: 480, padding: 24, boxSizing: 'border-box' }}>
      <Box
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12
        }}>
        <SText
          size="lg"
          weight="bold"
          fontColor="#20262D"
          title="File Details"
        />
        <IconButton size="small" onClick={onClose}>
          <CloseRoundedIcon htmlColor="#5D7083" style={{ fontSize: 22 }} />
        </IconButton>
      </Box>

      <Box style={{ marginTop: 4 }}>
        <SText
          size="sm"
          fontColor="#5D7083"
          noLineClamp
          title={`${failedFiles.length} of ${totalCount} files couldn't be processed`}
        />
      </Box>

      {failedFiles.length > 0 && (
        <Box
          style={{
            marginTop: 16,
            borderRadius: 12,
            border: '1px solid #ECEEF1',
            maxHeight: 220,
            overflow: 'auto'
          }}>
          {failedFiles.map((file, index) => (
            <Box
              key={file.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '12px 16px',
                borderTop: index === 0 ? 'none' : '1px solid #F2F3F5'
              }}>
              <Box style={{ flex: 1, minWidth: 0 }}>
                <SText
                  size="sm"
                  weight="semibold"
                  fontColor={FAILED_COLOR}
                  title={file?.file?.name || 'Untitled file'}
                />
              </Box>
              <Box
                style={{
                  flexShrink: 0,
                  padding: '2px 12px',
                  borderRadius: 999,
                  backgroundColor: '#FDD4D4'
                }}>
                <SText
                  size="xs"
                  weight="semibold"
                  fontColor={FAILED_COLOR}
                  title="Failed"
                />
              </Box>
              <Tooltip arrow title="Download Uploaded file">
                <span>
                  <IconButton
                    size="small"
                    aria-label="Download Uploaded file"
                    disabled={!file?.file?.url}
                    onClick={() =>
                      downloadOriginal(file?.file?.url, file?.file?.name)
                    }>
                    <DownloadIconV2
                      fill="#1E96FC"
                      style={{ width: 16, height: 16 }}
                    />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          ))}
        </Box>
      )}

      <Box
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          marginTop: 16,
          padding: 12,
          borderRadius: 12,
          backgroundColor: '#EBF5FF'
        }}>
        <Box
          style={{
            flexShrink: 0,
            width: 28,
            height: 28,
            borderRadius: '50%',
            background: '#1E96FC',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
          <InfoIconV2 fill="#FFFFFF" style={{ width: 20, height: 20 }} />
        </Box>
        <SText
          size="sm"
          fontColor="#01294B"
          noLineClamp
          title="Still stuck? Click Live Help and we'll take a look with you."
        />
      </Box>

      <Box
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          marginTop: 24
        }}>
        <SButton
          variant="text"
          color="default"
          onClick={onClose}
          style={{ color: '#5D7083', fontWeight: 600 }}>
          Cancel
        </SButton>
        <SButton
          variant="contained"
          onClick={openLiveHelp}
          style={{ borderRadius: 36, padding: '10px 24px', fontWeight: 600 }}>
          Live Help
        </SButton>
      </Box>
    </Box>
  );
};

export default FileDetailsDialog;
